import React, { useState, useEffect } from "react";
import { useGlobalContext } from '../../../../Context/GlobalContext';
import styles from './Reports.module.css';
import Topbar from '../Topbar/Topbar';
import Menubar from '../Menubar/Menubar';
import SalesReport from '../SalesReport/SalesReport';
import SalesSummaryReport from '../SalesSummaryReport/SalesSummaryReport';
import CancellationReport from '../CancellationReport/CancellationReport';
import LeaveReport from '../LeaveReport/LeaveReport';
import BreakReport from '../BreakReport/BreakReport';
import Payroll from '../Payroll/Payroll';
import CheckinReport from '../CheckinReport/CheckinReport';
import PaymentSummaryReport from '../PaymentSummaryReport/PaymentSummaryReport';
import PaymentDetailsReport from '../PaymentDetailsReport/PaymentDetailsReport';
import PluReport from '../PluReport/PluReport';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const NON_STAFF_REPORTS = ['sales', 'salesSummary', 'paymentSummary', 'plu'];
const shouldIncludeArchive = (reportId) =>
	!NON_STAFF_REPORTS.includes(reportId) && reportId !== 'paymentDetails';

const Reports = () => {
	const { selectedOutlet } = useGlobalContext();
	const [activeReport, setActiveReport] = useState('leave');
	const [filterParams, setFilterParams] = useState(null);
	const [hasInitialized, setHasInitialized] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isCustomPeriod, setIsCustomPeriod] = useState(false);
	const isSalesLikeReport =
		activeReport === 'sales' ||
		activeReport === 'salesSummary' ||
		activeReport === 'paymentSummary' ||
		activeReport === 'plu';
	const hasStaffFilter = !isSalesLikeReport;

	// Auto-generate report for today only on initial mount when outlet is available
	// This only runs once when the component first mounts
	useEffect(() => {
		if (selectedOutlet && !hasInitialized) {
			const todayParams = {
				outlet: selectedOutlet,
				date: 'today'
			};
			if (shouldIncludeArchive(activeReport)) {
				todayParams.archive = 1;
			}
			setFilterParams(todayParams);
			setHasInitialized(true);
		}
	}, [selectedOutlet, hasInitialized, activeReport]);

	// Keep filter params outlet in sync with selected outlet
	useEffect(() => {
		if (!selectedOutlet || !filterParams) {
			return;
		}
		if (filterParams.outlet !== selectedOutlet) {
			setFilterParams((prev) =>
				prev
					? {
						...prev,
						outlet: selectedOutlet,
						// PLU loads only on an explicit Generate, not on outlet change
						...(activeReport === 'plu' ? { generated: false } : {}),
					}
					: prev
			);
		}
	}, [selectedOutlet, filterParams, activeReport]);

	const handleGenerate = (params) => {
		// console.log('Generate report with params:', params);
		setFilterParams({ ...params, generated: true });
		// Track if custom time period is selected
		setIsCustomPeriod(params.date === 'custom');
	};

	const handleDateChange = (dateValue) => {
		// Track if custom time period is selected immediately when dropdown changes
		setIsCustomPeriod(dateValue === 'custom');
	};

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	const closeMenu = () => {
		setIsMenuOpen(false);
	};

	const handleReportChange = (reportId) => {
		setActiveReport(reportId);
		setIsCustomPeriod(false);
		const defaultDate = reportId === 'plu' ? 'monthToDate' : 'today';
		const defaultParams = {
			outlet: selectedOutlet,
			date: defaultDate
		};
		if (shouldIncludeArchive(reportId)) {
			defaultParams.archive = 1;
		}
		setFilterParams(defaultParams);
		closeMenu();
	};

	return (
		<>
			<ToastContainer position="top-right" autoClose={3000} />
			<div className={`${styles.reportsContainer} header-sapce`}>
				{isMenuOpen && <div className={styles.overlay} onClick={closeMenu}></div>}
				<div className={`${styles.menubarWrapper} ${isMenuOpen ? styles.menuOpen : ''}`}>
					<Menubar activeReport={activeReport} setActiveReport={handleReportChange} />
				</div>
				<div className={`${styles.rightSection} ${activeReport === 'break' ? styles.breakRightSection : ''}`}>
					<button
						className={styles.hamburgerButton}
						onClick={toggleMenu}
						aria-label="Toggle menu"
						style={{
						top:
							activeReport === 'break'
								? isCustomPeriod
									? 'calc(var(--headerHeight) + 436px)'
									: 'calc(var(--headerHeight) + 335px)'
								: activeReport === 'paymentSummary'
									? isCustomPeriod
										? 'calc(var(--headerHeight) + 285px)'
										: 'calc(var(--headerHeight) + 185px)'
								: activeReport === 'paymentDetails'
									? isCustomPeriod
										? 'calc(var(--headerHeight) + 335px)'
										: 'calc(var(--headerHeight) + 235px)'
								: isCustomPeriod && !hasStaffFilter
									? 'calc(var(--headerHeight) + 280px)'
									: isCustomPeriod
										? 'calc(var(--headerHeight) + 385px)'
										: !hasStaffFilter
											? 'calc(var(--headerHeight) + 180px)'
											: 'calc(var(--headerHeight) + 280px)',
						}}
					>
						<span className={styles.hamburgerLine}></span>
						<span className={styles.hamburgerLine}></span>
						<span className={styles.hamburgerLine}></span>
					</button>
					<Topbar onGenerate={handleGenerate} activeReport={activeReport} onDateChange={handleDateChange} />
					<div className={styles.reportContent}>
						{activeReport === 'sales' && <SalesReport filterParams={filterParams} />}
						{activeReport === 'salesSummary' && <SalesSummaryReport filterParams={filterParams} />}
						{activeReport === 'plu' && <PluReport filterParams={filterParams} />}
						{activeReport === 'cancellation' && <CancellationReport filterParams={filterParams} />}
						{activeReport === 'checkin' && <CheckinReport filterParams={filterParams} />}
						{activeReport === 'paymentSummary' && <PaymentSummaryReport filterParams={filterParams} />}
						{activeReport === 'paymentDetails' && <PaymentDetailsReport filterParams={filterParams} />}
						{activeReport === 'leave' && <LeaveReport filterParams={filterParams} />}
						{activeReport === 'break' && <BreakReport filterParams={filterParams} />}
						{activeReport === 'payroll' && <Payroll filterParams={filterParams} />}
					</div>
				</div>
			</div>
		</>
	);
};

export default Reports;
